import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    DollarSign, Calendar, CheckCircle, Clock,
    ChevronLeft, ChevronRight, Users
} from 'lucide-react';
import { isAdminUser } from '../utils/adminClaim';

const AdminSettlementManagement = ({ userInfo }) => {
    const navigate = useNavigate();
    const [settlements, setSettlements] = useState([]);
    const [orders, setOrders] = useState([]);
    const [sellerMap, setSellerMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1);
    });

    const formatMonth = (date) => {
        return `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
    };

    const getMonthRange = useCallback(() => {
        const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
        const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0, 23, 59, 59, 999);
        return {
            start: Timestamp.fromDate(start),
            end: Timestamp.fromDate(end)
        };
    }, [currentMonth]);

    const goToPrevMonth = () => {
        setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    };

    const goToNextMonth = () => {
        const now = new Date();
        const nextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);
        if (nextMonth <= new Date(now.getFullYear(), now.getMonth() + 1, 1)) {
            setCurrentMonth(nextMonth);
        }
    };

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const { start, end } = getMonthRange();

            // 1. Load all settlement records
            const settlementsSnapshot = await getDocs(collection(db, 'marketplaceSettlements'));
            const settlementsData = settlementsSnapshot.docs.map(d => ({
                id: d.id,
                ...d.data()
            }));
            setSettlements(settlementsData);

            // 2. Load delivered orders for the selected month
            const ordersQuery = query(
                collection(db, 'marketplaceOrders'),
                where('status', '==', 'delivered'),
                where('createdAt', '>=', start),
                where('createdAt', '<=', end)
            );
            const ordersSnapshot = await getDocs(ordersQuery);
            const ordersData = ordersSnapshot.docs.map(d => ({
                id: d.id,
                ...d.data()
            }));
            setOrders(ordersData);

            // 3. Load seller information
            const sellerIds = [...new Set(ordersData.map(o => o.sellerId).filter(Boolean))];
            const sellerData = {};

            if (sellerIds.length > 0) {
                // Firestore 'in' query supports max 10 items at a time
                const chunks = [];
                for (let i = 0; i < sellerIds.length; i += 10) {
                    chunks.push(sellerIds.slice(i, i + 10));
                }

                for (const chunk of chunks) {
                    const sellersQuery = query(
                        collection(db, 'users'),
                        where('__name__', 'in', chunk)
                    );
                    const sellersSnapshot = await getDocs(sellersQuery);
                    sellersSnapshot.docs.forEach(d => {
                        sellerData[d.id] = { id: d.id, ...d.data() };
                    });
                }
            }
            setSellerMap(sellerData);

        } catch (error) {
            console.error('정산 데이터 로드 실패:', error);
            alert('정산 데이터를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, [getMonthRange]);

    useEffect(() => {
        if (!userInfo) {
            alert('로그인이 필요합니다.');
            navigate('/login');
            return;
        }
        if (!isAdminUser(userInfo)) {
            return;
        }
        loadData();
    }, [userInfo, navigate, loadData]);

    // Build seller settlement summary for current month
    const getSellerSettlements = useCallback(() => {
        const monthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;

        // Group orders by seller
        const sellerOrderMap = {};
        orders.forEach(order => {
            const sellerId = order.sellerId;
            if (!sellerId) return;
            if (!sellerOrderMap[sellerId]) {
                sellerOrderMap[sellerId] = [];
            }
            sellerOrderMap[sellerId].push(order);
        });

        // Build settlement data per seller
        const sellerSettlements = Object.keys(sellerOrderMap).map(sellerId => {
            const sellerOrders = sellerOrderMap[sellerId];
            const seller = sellerMap[sellerId] || {};
            const totalSales = sellerOrders.reduce((sum, o) => sum + (o.totalAmount || o.price * (o.quantity || 1) || 0), 0);
            const feeRate = seller.feeRate != null ? seller.feeRate / 100 : 0.10; // default 10%
            const commission = Math.round(totalSales * feeRate);
            const netAmount = totalSales - commission;

            // Check if settlement record exists for this seller and month
            const existingSettlement = settlements.find(
                s => s.sellerId === sellerId && s.monthKey === monthKey
            );

            return {
                sellerId,
                sellerName: seller.storeName || seller.businessName || seller.companyName || seller.name || '알 수 없는 판매자',
                totalSales,
                feeRatePercent: feeRate * 100,
                commission,
                netAmount,
                orderCount: sellerOrders.length,
                status: existingSettlement?.status || 'pending',
                settlementId: existingSettlement?.id || null,
                completedAt: existingSettlement?.completedAt || null
            };
        });

        // Sort by total sales descending
        sellerSettlements.sort((a, b) => b.totalSales - a.totalSales);

        return sellerSettlements;
    }, [orders, sellerMap, settlements, currentMonth]);

    const sellerSettlements = getSellerSettlements();

    // Summary totals
    const totals = sellerSettlements.reduce((acc, s) => ({
        totalSales: acc.totalSales + s.totalSales,
        totalCommission: acc.totalCommission + s.commission,
        totalNet: acc.totalNet + s.netAmount,
        totalOrders: acc.totalOrders + s.orderCount
    }), { totalSales: 0, totalCommission: 0, totalNet: 0, totalOrders: 0 });

    const handleMarkAsPaid = async (settlement) => {
        const monthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`;
        if (!window.confirm(`${settlement.sellerName}의 ${formatMonth(currentMonth)} 정산을 완료 처리하시겠습니까?\n\n정산 금액: ${formatPrice(settlement.netAmount)}원`)) {
            return;
        }

        try {
            setProcessing(settlement.sellerId);

            if (settlement.settlementId) {
                // Update existing settlement record
                await updateDoc(doc(db, 'marketplaceSettlements', settlement.settlementId), {
                    status: 'completed',
                    completedAt: serverTimestamp(),
                    completedBy: userInfo.uid,
                    totalSales: settlement.totalSales,
                    commission: settlement.commission,
                    netAmount: settlement.netAmount,
                    updatedAt: serverTimestamp()
                });
            } else {
                // Create new settlement record via updateDoc on a new doc ref
                const { doc: docRef, setDoc } = await import('firebase/firestore');
                const newSettlementRef = docRef(collection(db, 'marketplaceSettlements'));
                await setDoc(newSettlementRef, {
                    sellerId: settlement.sellerId,
                    sellerName: settlement.sellerName,
                    monthKey: monthKey,
                    totalSales: settlement.totalSales,
                    feeRate: settlement.feeRatePercent,
                    commission: settlement.commission,
                    netAmount: settlement.netAmount,
                    orderCount: settlement.orderCount,
                    status: 'completed',
                    completedAt: serverTimestamp(),
                    completedBy: userInfo.uid,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
            }

            alert(`${settlement.sellerName}의 정산이 완료 처리되었습니다.`);
            await loadData();
        } catch (error) {
            console.error('정산 처리 실패:', error);
            alert('정산 처리에 실패했습니다.');
        } finally {
            setProcessing(null);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('ko-KR').format(price || 0);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Access denied view
    if (userInfo && !isAdminUser(userInfo)) {
        return (
            <div style={styles.accessDenied}>
                <div style={styles.accessDeniedCard}>
                    <div style={styles.accessDeniedIcon}>
                        <Users size={48} color="#ef4444" />
                    </div>
                    <h2 style={styles.accessDeniedTitle}>접근 권한 없음</h2>
                    <p style={styles.accessDeniedText}>관리자만 접근할 수 있는 페이지입니다.</p>
                    <button
                        style={styles.accessDeniedButton}
                        onClick={() => navigate('/dashboard')}
                    >
                        대시보드로 돌아가기
                    </button>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner} />
                <p style={styles.loadingText}>정산 데이터를 불러오는 중...</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <DollarSign size={32} color="#6366f1" />
                    <div>
                        <h1 style={styles.title}>정산 관리</h1>
                        <p style={styles.subtitle}>판매자별 월별 정산을 관리합니다</p>
                    </div>
                </div>
            </div>

            {/* Month Navigation */}
            <div style={styles.monthNav}>
                <button style={styles.monthButton} onClick={goToPrevMonth}>
                    <ChevronLeft size={20} />
                </button>
                <div style={styles.monthDisplay}>
                    <Calendar size={20} color="#6366f1" />
                    <span style={styles.monthText}>{formatMonth(currentMonth)}</span>
                </div>
                <button
                    style={{
                        ...styles.monthButton,
                        opacity: new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1) > new Date() ? 0.4 : 1,
                        cursor: new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1) > new Date() ? 'not-allowed' : 'pointer'
                    }}
                    onClick={goToNextMonth}
                    disabled={new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1) > new Date()}
                >
                    <ChevronRight size={20} />
                </button>
            </div>

            {/* Stats Cards */}
            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, backgroundColor: '#e0e7ff' }}>
                        <Users size={20} color="#6366f1" />
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statLabel}>판매자 수</div>
                        <div style={styles.statValue}>{sellerSettlements.length}명</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, backgroundColor: '#dbeafe' }}>
                        <DollarSign size={20} color="#3b82f6" />
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statLabel}>총 매출</div>
                        <div style={styles.statValue}>{formatPrice(totals.totalSales)}원</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, backgroundColor: '#fef3c7' }}>
                        <DollarSign size={20} color="#f59e0b" />
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statLabel}>총 수수료</div>
                        <div style={styles.statValue}>{formatPrice(totals.totalCommission)}원</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, backgroundColor: '#d1fae5' }}>
                        <CheckCircle size={20} color="#10b981" />
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statLabel}>정산 완료</div>
                        <div style={styles.statValue}>
                            {sellerSettlements.filter(s => s.status === 'completed').length}/{sellerSettlements.length}
                        </div>
                    </div>
                </div>
            </div>

            {/* Settlement Table */}
            <div style={styles.tableContainer}>
                {sellerSettlements.length === 0 ? (
                    <div style={styles.emptyState}>
                        <DollarSign size={48} color="#cbd5e1" />
                        <p style={styles.emptyText}>{formatMonth(currentMonth)}에 해당하는 정산 내역이 없습니다.</p>
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>판매자</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>주문 수</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>총 매출</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>수수료율</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>수수료</th>
                                <th style={{ ...styles.th, textAlign: 'right' }}>정산 금액</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>상태</th>
                                <th style={{ ...styles.th, textAlign: 'center' }}>관리</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sellerSettlements.map(settlement => (
                                <tr key={settlement.sellerId} style={styles.tr}>
                                    <td style={styles.td}>
                                        <div style={styles.sellerInfo}>
                                            <div style={styles.sellerAvatar}>
                                                <Users size={16} color="#6366f1" />
                                            </div>
                                            <span style={styles.sellerName}>{settlement.sellerName}</span>
                                        </div>
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                        {settlement.orderCount}건
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '600' }}>
                                        {formatPrice(settlement.totalSales)}원
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                        <span style={styles.feeRateBadge}>
                                            {settlement.feeRatePercent}%
                                        </span>
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'right', color: '#ef4444' }}>
                                        -{formatPrice(settlement.commission)}원
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: '700', color: '#1e293b' }}>
                                        {formatPrice(settlement.netAmount)}원
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                        {settlement.status === 'completed' ? (
                                            <span style={styles.badgeCompleted}>
                                                <CheckCircle size={14} />
                                                정산완료
                                            </span>
                                        ) : (
                                            <span style={styles.badgePending}>
                                                <Clock size={14} />
                                                대기중
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                        {settlement.status === 'completed' ? (
                                            <div style={styles.completedInfo}>
                                                <span style={styles.completedDate}>
                                                    {formatDate(settlement.completedAt)}
                                                </span>
                                            </div>
                                        ) : (
                                            <button
                                                style={{
                                                    ...styles.paidButton,
                                                    opacity: processing === settlement.sellerId ? 0.6 : 1,
                                                    cursor: processing === settlement.sellerId ? 'not-allowed' : 'pointer'
                                                }}
                                                onClick={() => handleMarkAsPaid(settlement)}
                                                disabled={processing === settlement.sellerId}
                                            >
                                                {processing === settlement.sellerId ? (
                                                    '처리중...'
                                                ) : (
                                                    <>
                                                        <CheckCircle size={14} />
                                                        정산 완료
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}

                            {/* Summary Row */}
                            <tr style={styles.summaryRow}>
                                <td style={{ ...styles.td, fontWeight: '700', color: '#1e293b' }}>
                                    합계
                                </td>
                                <td style={{ ...styles.td, textAlign: 'center', fontWeight: '700' }}>
                                    {totals.totalOrders}건
                                </td>
                                <td style={{ ...styles.td, textAlign: 'right', fontWeight: '700', color: '#1e293b' }}>
                                    {formatPrice(totals.totalSales)}원
                                </td>
                                <td style={{ ...styles.td, textAlign: 'center' }}>-</td>
                                <td style={{ ...styles.td, textAlign: 'right', fontWeight: '700', color: '#ef4444' }}>
                                    -{formatPrice(totals.totalCommission)}원
                                </td>
                                <td style={{ ...styles.td, textAlign: 'right', fontWeight: '700', color: '#6366f1', fontSize: '16px' }}>
                                    {formatPrice(totals.totalNet)}원
                                </td>
                                <td style={{ ...styles.td, textAlign: 'center' }}>
                                    <span style={styles.summaryStatus}>
                                        {sellerSettlements.filter(s => s.status === 'completed').length}/{sellerSettlements.length} 완료
                                    </span>
                                </td>
                                <td style={styles.td}></td>
                            </tr>
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: {
        padding: '24px',
        maxWidth: '1400px',
        margin: '0 auto'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        gap: '16px',
        color: '#64748b'
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid #e2e8f0',
        borderTop: '4px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    loadingText: {
        fontSize: '15px',
        color: '#64748b'
    },
    // Access Denied
    accessDenied: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f8fafc'
    },
    accessDeniedCard: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '48px',
        textAlign: 'center',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
        maxWidth: '400px',
        width: '90%'
    },
    accessDeniedIcon: {
        marginBottom: '20px'
    },
    accessDeniedTitle: {
        fontSize: '22px',
        fontWeight: '700',
        color: '#1e293b',
        margin: '0 0 12px 0'
    },
    accessDeniedText: {
        fontSize: '15px',
        color: '#64748b',
        margin: '0 0 24px 0',
        lineHeight: '1.6'
    },
    accessDeniedButton: {
        padding: '12px 32px',
        backgroundColor: '#6366f1',
        color: 'white',
        border: 'none',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer'
    },
    // Header
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px'
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
    },
    title: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#1e293b',
        margin: 0
    },
    subtitle: {
        fontSize: '14px',
        color: '#64748b',
        margin: '4px 0 0 0'
    },
    // Month Navigation
    monthNav: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    monthButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        backgroundColor: '#f1f5f9',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        color: '#475569',
        transition: 'background-color 0.2s'
    },
    monthDisplay: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    monthText: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1e293b'
    },
    // Stats Grid
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
    },
    statCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    statIcon: {
        width: '48px',
        height: '48px',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    statContent: {
        flex: 1
    },
    statLabel: {
        fontSize: '13px',
        color: '#64748b',
        marginBottom: '4px'
    },
    statValue: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1e293b'
    },
    // Table
    tableContainer: {
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'auto'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        minWidth: '900px'
    },
    th: {
        padding: '16px',
        textAlign: 'left',
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderBottom: '2px solid #e2e8f0',
        whiteSpace: 'nowrap'
    },
    tr: {
        borderBottom: '1px solid #f1f5f9',
        transition: 'background-color 0.15s'
    },
    td: {
        padding: '16px',
        fontSize: '14px',
        color: '#1e293b',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap'
    },
    sellerInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    sellerAvatar: {
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        backgroundColor: '#e0e7ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    sellerName: {
        fontWeight: '600',
        color: '#1e293b'
    },
    feeRateBadge: {
        display: 'inline-block',
        padding: '3px 10px',
        backgroundColor: '#f1f5f9',
        color: '#64748b',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600'
    },
    badgeCompleted: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 12px',
        backgroundColor: '#d1fae5',
        color: '#059669',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600'
    },
    badgePending: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 12px',
        backgroundColor: '#fef3c7',
        color: '#92400e',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600'
    },
    paidButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#6366f1',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        whiteSpace: 'nowrap'
    },
    completedInfo: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    completedDate: {
        fontSize: '12px',
        color: '#94a3b8'
    },
    // Summary Row
    summaryRow: {
        backgroundColor: '#f8fafc',
        borderTop: '2px solid #e2e8f0'
    },
    summaryStatus: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#6366f1'
    },
    // Empty State
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        color: '#94a3b8',
        gap: '12px'
    },
    emptyText: {
        fontSize: '15px',
        color: '#94a3b8',
        margin: 0
    }
};

export default AdminSettlementManagement;
